<!-- 
Panel showing levy collection tasks.

Applicable ones are shown first, and inapplicable ones can optionally be displayed if the user wants to
see why they are not applicable at the current time.
-->
<template>
  <div class="pb-3 mr-4">
    <div v-if="state.loading" class="text-xs-center my-5 py-5 mr-5 pr-5">
      <v-progress-circular size="120" width="7" color="grey" indeterminate>Loading...</v-progress-circular>
    </div>
    <v-scale-transition>
      <div v-if="!state.loading">
        <p v-if="noApplicableTasks && !search" class="mt-4 ml-4 grey--text text--darken-1">
          Currently there are no applicable tasks.
        </p>
        <p v-if="applicableTasks.length === 0 && inapplicableTasks.length === 0 && optionalTasks.length === 0" class="mt-4 ml-4 grey--text text--darken-1">
          No matching collection tasks found.
        </p>
        <div v-for="task in applicableTasks" :key="task.type">
          <levy-collection-task-card :task="task" @run="runTask" />
        </div>
        <div v-if="applicableTasks.length && optionalTasks.length" class="text-xs-center">
          <v-icon class="grey--text">more_horiz</v-icon>
        </div>
        <h2 v-if="optionalTasks.length" class="title font-weight-regular mb-3 mt-3 grey--text text--darken-1">Optional collection tasks</h2>
        <div v-for="task in optionalTasks" :key="task.type">
          <levy-collection-task-card :task="task" muted @run="runTask" />
        </div>
        <v-btn 
          v-if="!search"
          flat class="mt-2 ml-3" small
          color="primary"
          @click="showNotApplicable=!showNotApplicable"
        >
          Show unavailable tasks ({{ inapplicableTasks.length }})
          <our-expand-icon :expanded="showNotApplicable"/>
        </v-btn>
        <div v-if="showNotApplicable || search" class=" ml-3 mr-4">
          <div v-for="task in inapplicableTasks" :key="task.type">
            <div class="mt-4 mb-5">
              <h3 :class="getTextColour(task)" class="title mb-3 mt-4">
                {{ lookups.levyCollectionTaskTypeDescriptions[task.type] }}
              </h3>
              <p :class="getTextColour(task)">
                {{ task.description }}
              </p>
              <p :class="getTextColour(task)" class="mt-2">
                Not applicable because {{ task.notApplicableReason }}.
              </p>
            </div>
          </div>
        </div>
      </div>
    </v-scale-transition>
    <run-task-dialog v-model="showRunTaskDialog" :levy-collection-id="levyCollectionId" :task="taskToRun"/>
    <manual-upload-dialog 
      v-model="showFinancialStrengthParameterUploadDialog"
      :levy-collection-id="levyCollectionId"
      :show-previous-years-data-checkbox="taskToRun.showPreviousYearsDataOption"
      description="Upload offline levy parameters"
      endpoint-suffix="offline-levy-parameters"
    />
    <manual-upload-dialog 
      v-model="showInScopeProvidersUploadDialog"
      :levy-collection-id="levyCollectionId"
      :show-previous-years-data-checkbox="taskToRun.showPreviousYearsDataOption"
      description="Upload in-scope providers"
      endpoint-suffix="in-scope"
    />
    <manual-upload-dialog 
      v-model="showLevyParametersUploadDialog"
      :levy-collection-id="levyCollectionId"
      :show-previous-years-data-checkbox="taskToRun.showPreviousYearsDataOption"
      description="Upload levy parameters"
      endpoint-suffix="basic-levy-parameters"
    />
  </div>
</template>

<script>
import * as action from "@/store/levy-collections/action-types";
import lookupMixins from "@/mixins/lookup.mixins";
import taskMixins from "@/mixins/task.mixins";
import LevyCollectionTaskCard from "@/components/levy-collections/LevyCollectionTaskCard.vue";
import RunTaskDialog from "@/components/levy-collections/RunTaskDialog.vue";
import ManualUploadDialog from "@/components/levy-collection-providers/ManualUploadDialog.vue";

export default {
  components: { LevyCollectionTaskCard, RunTaskDialog, ManualUploadDialog },
  mixins: [lookupMixins, taskMixins],
  props: {
    levyCollectionId: {
      required: true,
      type: Number
    },
    search: {
      type: String,
      default: ""
    }
  },
  data() {
    return {
      showNotApplicable: false,
      showRunTaskDialog: false,
      showFinancialStrengthParameterUploadDialog: false,
      showInScopeProvidersUploadDialog: false,
      showLevyParametersUploadDialog: false,
      taskToRun: {
        type: null,
        description: null,
        showPreviousYearsDataOption: false
      }
    };
  },
  computed: {
    state() {
      return this.$store.state.levyCollections.tasks;
    },
    loaded() {
      return this.state.loaded;
    },
    tasks() {
      return this.state.data;
    },
    applicableTasks() {
      return this.searchTasks(
        this.tasks.filter(t => t.isApplicable && !t.optional),
        this.lookups.levyCollectionTaskTypeDescriptions,
        this.search
      );
    },
    optionalTasks() {
      return this.searchTasks(
        this.tasks.filter(t => t.isApplicable && t.optional),
        this.lookups.levyCollectionTaskTypeDescriptions,
        this.search
      );
    },
    inapplicableTasks() {
      return this.searchTasks(
        this.tasks.filter(t => !t.isApplicable && !t.hideUntilApplicable),
        this.lookups.levyCollectionTaskTypeDescriptions,
        this.search
      );
    },
    noApplicableTasks() {
      return this.tasks.every(t => !t.isApplicable);
    }
  },
  watch: {
    levyCollectionId() {
      this.refresh();
    },
    loaded() {
      this.refresh();
    }
  },
  mounted() {
    this.refresh();
  },
  methods: {
    refresh() {
      if ((!this.state.loaded && !this.state.loading) || this.state.levyCollectionId != this.levyCollectionId) {
        this.$store.dispatch(action.TASKS_GET, this.levyCollectionId);
      }
    },
    runTask(task) {
      this.taskToRun = task;
      if (task.type == "UploadOfflineLevyParameters") {
        this.showFinancialStrengthParameterUploadDialog = true;
      } else if (task.type == "UploadInScopeProviders") {
        this.showInScopeProvidersUploadDialog = true;
      } else if (task.type == "UploadLevyParameters") {
        this.showLevyParametersUploadDialog = true;
      } else {
        this.showRunTaskDialog = true;
      }
    },
    getTextColour(task) {
      return task.isApplicable ? "" : "grey--text";
    }
  }
};
</script>

<style lang="scss">
.subheading .lock {
  line-height: 19px;
}
.title .lock {
  line-height: 22px;
}
</style>